import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useReactTable, getCoreRowModel, flexRender } from "@tanstack/react-table";
import { Plus, Search } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@trillion/ui/components/ui/button";
import { Input } from "@trillion/ui/components/ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@trillion/ui/components/ui/select";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@trillion/ui/components/ui/table";
import { PageHeader } from "@trillion/ui/components/admin/page-header";
import { DataTableWrapper } from "@trillion/ui/components/admin/data-table-wrapper";
import { ConfirmDialog } from "@trillion/ui/components/admin/confirm-dialog";
import { useDataTableState } from "@trillion/ui";
import { adminApi } from "@/lib/admin-api";
import { describeApiError } from "@/lib/api-error";
import { useNetworkOptions } from "@/hooks/use-network-options";
import { useBrandOptions } from "@/hooks/use-brand-options";
import { getProductColumns } from "@/components/products/product-columns";
import { CatalogCountsPanel } from "@/components/products/catalog-counts-panel";
import type { Product } from "@/types";

const PAGE_SIZE = 20;
const ALL = "all";
// Matches useDataTableState's own default search debounce, so the two text surfaces settle alike.
const FILTER_DEBOUNCE_MS = 300;

function useDebounced<T>(value: T, delayMs: number): T {
  const [settled, setSettled] = useState(value);
  useEffect(() => {
    const id = window.setTimeout(() => setSettled(value), delayMs);
    return () => window.clearTimeout(id);
  }, [value, delayMs]);
  return settled;
}

export function ProductsPage() {
  const navigate = useNavigate();
  const { networks } = useNetworkOptions();
  const [networkFilter, setNetworkFilter] = useState<string>(ALL);
  const [brandFilter, setBrandFilter] = useState<string>(ALL);
  // Both variant narrowers are optional and free-text: clearing one restores the unfiltered listing,
  // because toQuery drops "" exactly as it drops undefined. Debounced before they reach `filters`:
  // useDataTableState debounces `search` only and refetches on every filter *value* change, so an
  // un-debounced text box spends one admin request per keystroke against a 60/min budget. The existing
  // network/brand filters are Selects, so the gap never showed until a text filter landed here.
  const [catalogFilter, setCatalogFilter] = useState<string>("");
  const [itemGroupFilter, setItemGroupFilter] = useState<string>("");
  const catalogId = useDebounced(catalogFilter, FILTER_DEBOUNCE_MS);
  const itemGroupId = useDebounced(itemGroupFilter, FILTER_DEBOUNCE_MS);

  const sourceId = networkFilter === ALL ? undefined : Number(networkFilter);
  const brandId = brandFilter === ALL ? undefined : Number(brandFilter);
  // Brand filter options cascade off the selected network.
  const { brands, isLoading: brandsLoading } = useBrandOptions(sourceId ?? null);

  function handleNetworkFilterChange(value: string) {
    setNetworkFilter(value);
    setBrandFilter(ALL); // a brand belongs to one network — reset on network change
  }

  const t = useDataTableState<Product>({
    fetcher: async (p) => {
      const filters = p.filters as
        | { sourceId?: number; brandId?: number; catalogId?: string; itemGroupId?: string }
        | undefined;
      const result = await adminApi.products.list({
        page: p.page,
        pageSize: p.pageSize,
        search: p.search || undefined,
        sourceId: filters?.sourceId,
        brandId: filters?.brandId,
        catalogId: filters?.catalogId,
        itemGroupId: filters?.itemGroupId,
        sortBy: p.sortBy,
        sortDesc: p.sortDesc,
      });
      return { items: result.items, totalCount: result.total_count };
    },
    onDelete: async (product) => {
      await adminApi.products.delete(product.product_id);
      toast.success("Product deleted");
    },
    filters: { sourceId, brandId, catalogId, itemGroupId },
    pageSize: PAGE_SIZE,
    onError: (err) =>
      toast.error("Request failed", {
        description: describeApiError(err),
      }),
  });

  const { setDeleteTarget } = t;
  const columns = useMemo(
    () =>
      getProductColumns({
        onEdit: (product) => navigate(`/products/${product.product_id}`),
        onDelete: (product) => setDeleteTarget(product),
      }),
    [navigate, setDeleteTarget]
  );

  const table = useReactTable({
    data: t.data,
    columns,
    getCoreRowModel: getCoreRowModel(),
  });

  return (
    <div className="flex flex-col gap-6 p-6">
      <PageHeader
        title="Products"
        description="Manage your product catalog"
        actions={
          <Button onClick={() => navigate("/products/new")}>
            <Plus className="mr-2 size-4" />
            New Product
          </Button>
        }
      />

      <CatalogCountsPanel />

      <DataTableWrapper
        title="All Products"
        description={`${t.totalCount} product${t.totalCount !== 1 ? "s" : ""} total`}
        isLoading={t.isLoading}
        toolbar={
          <div className="flex flex-wrap items-center gap-4">
            <div className="relative max-w-sm flex-1">
              <Search className="absolute left-3 top-1/2 size-4 -translate-y-1/2 text-muted-foreground" />
              <Input
                placeholder="Search products..."
                value={t.search}
                onChange={(e) => t.setSearch(e.target.value)}
                className="pl-9"
              />
            </div>
            <Select value={networkFilter} onValueChange={handleNetworkFilterChange}>
              <SelectTrigger className="w-[180px]">
                <SelectValue placeholder="All networks" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value={ALL}>All networks</SelectItem>
                {networks.map((n) => (
                  <SelectItem key={n.source_id} value={String(n.source_id)}>
                    {n.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            <Select
              value={brandFilter}
              onValueChange={setBrandFilter}
              disabled={sourceId === undefined || brandsLoading}
            >
              <SelectTrigger className="w-[180px]">
                <SelectValue
                  placeholder={sourceId === undefined ? "All brands" : "Filter by brand"}
                />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value={ALL}>All brands</SelectItem>
                {brands.map((b) => (
                  <SelectItem key={b.brand_id} value={String(b.brand_id)}>
                    {b.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            <Input
              placeholder="Filter by catalog id"
              aria-label="Filter by catalog id"
              className="w-[180px]"
              value={catalogFilter}
              onChange={(e) => setCatalogFilter(e.target.value)}
            />
            <Input
              placeholder="Filter by item group id"
              aria-label="Filter by item group id"
              className="w-[180px]"
              value={itemGroupFilter}
              onChange={(e) => setItemGroupFilter(e.target.value)}
            />
          </div>
        }
        pagination={
          <div className="flex items-center justify-between">
            <p className="text-sm text-muted-foreground">
              Page {t.page} of {t.totalPages}
            </p>
            <div className="flex gap-2">
              <Button
                variant="outline"
                size="sm"
                onClick={() => t.setPage(Math.max(1, t.page - 1))}
                disabled={t.page <= 1}
              >
                Previous
              </Button>
              <Button
                variant="outline"
                size="sm"
                onClick={() => t.setPage(t.page + 1)}
                disabled={t.page >= t.totalPages}
              >
                Next
              </Button>
            </div>
          </div>
        }
      >
        <Table>
          <TableHeader>
            {table.getHeaderGroups().map((headerGroup) => (
              <TableRow key={headerGroup.id}>
                {headerGroup.headers.map((header) => (
                  <TableHead key={header.id}>
                    {header.isPlaceholder
                      ? null
                      : flexRender(header.column.columnDef.header, header.getContext())}
                  </TableHead>
                ))}
              </TableRow>
            ))}
          </TableHeader>
          <TableBody>
            {table.getRowModel().rows.length === 0 ? (
              <TableRow>
                <TableCell colSpan={columns.length} className="h-24 text-center">
                  No products found.
                </TableCell>
              </TableRow>
            ) : (
              table.getRowModel().rows.map((row) => (
                <TableRow key={row.id}>
                  {row.getVisibleCells().map((cell) => (
                    <TableCell key={cell.id}>
                      {flexRender(cell.column.columnDef.cell, cell.getContext())}
                    </TableCell>
                  ))}
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </DataTableWrapper>

      <ConfirmDialog
        open={t.deleteTarget !== null}
        onOpenChange={(open) => {
          if (!open) t.setDeleteTarget(null);
        }}
        title="Delete Product"
        description={`Are you sure you want to delete "${t.deleteTarget?.name}"? This action cannot be undone.`}
        confirmLabel="Delete"
        onConfirm={t.confirmDelete}
        variant="destructive"
        isLoading={t.isDeleting}
      />
    </div>
  );
}
